import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function formatCurrency(amount: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(amount);
}

/**
 * Formats a dollar amount with no cents, rounding to the nearest dollar.
 * Used for glanceable summary tiles where cents are noise.
 *
 * Examples:
 * - formatWholeDollars(1014400) => "$1,014,400"
 * - formatWholeDollars(4190.49) => "$4,190"
 * - formatWholeDollars(0) => "$0"
 */
export function formatWholeDollars(amount: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

/**
 * Formats currency with parentheses for negative numbers instead of minus signs.
 * This is the app-wide standard for displaying negative currency values.
 *
 * Examples:
 * - formatCurrencyWithParentheses(1234.56) => "$1,234.56"
 * - formatCurrencyWithParentheses(-1234.56) => "($1,234.56)"
 */
export function formatCurrencyWithParentheses(amount: number): string {
  const isNegative = amount < 0;
  const absAmount = Math.abs(amount);
  const formatted = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(absAmount);

  return isNegative ? `(${formatted})` : formatted;
}

/*
Strips out all non-numeric characters except decimal points from a string
*/
export function cleanNumbers(value: string | undefined | null): string {
  if (!value) return "";
  const cleaned = value.replace(/[^\d.]/g, "");
  const parts = cleaned.split(".");
  return parts.length > 1 ? `${parts[0]}.${parts.slice(1).join("")}` : cleaned;
}

/**
 * Converts a numeric value to a string for use in currency/number inputs.
 * Handles null/undefined gracefully.
 */
export function numericValueToInputString(value: number | null | undefined): string {
  if (value === null || value === undefined) return ''
  return String(value)
}

/**
 * "1.2 MB" / "340 KB" for a byte count; empty string for null/0 so a missing
 * size renders as nothing rather than "0 Bytes".
 */
export function formatFileSize(bytes: number | null | undefined): string {
  if (!bytes || bytes <= 0) return ''
  const units = ['Bytes', 'KB', 'MB', 'GB']
  const unitIndex = Math.min(
    units.length - 1,
    Math.floor(Math.log(bytes) / Math.log(1024))
  )
  const value = Math.round((bytes / Math.pow(1024, unitIndex)) * 100) / 100
  return `${value} ${units[unitIndex]}`
}

export function truncateText(
  text: string | null | undefined,
  maxChars: number,
): string {
  if (!text) return ''
  if (text.length <= maxChars) return text
  return text.slice(0, maxChars).trimEnd() + '…'
}

/**
 * Title Case words for an enum-style value, e.g. `ORIGINATION_POINTS` →
 * `Origination Points`. Client-safe; used wherever a raw enum would otherwise
 * reach the screen.
 */
export function humanizeEnumLabel(value: string): string {
  return value
    .toLowerCase()
    .split('_')
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}
